/* eslint-disable react/require-default-props */
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  ambassadorFeedbackPath,
  ambassadorToolPath,
  AmbassadorToolState,
  findAmbassadorToolState,
  formatAmbassadorDate,
  isAwaitingRelease,
} from 'services/ambassador';
import { AMBASSADOR_TOOLS, AmbassadorTool, AmbassadorToolCard } from 'services/ambassador_tools';
import { featureIcon, isColoredIcon } from 'services/featureIcon';
import { ApplicationState } from 'store';
import { ProfileState } from 'store/modules/profile_store/types';
import { Container } from './styles';

interface ToolCardContentProps {
  tool: AmbassadorToolCard;
  step: number;
  note?: string;
  /** Motivo do bloqueio, em destaque mesmo no card esmaecido. */
  alert?: string;
  feedbackSent?: boolean;
}

const ToolIcon: React.FC<{ icon?: string }> = ({ icon }) =>
  icon ? (
    <img
      className={`tool-icon${isColoredIcon(icon) ? ' tool-icon-colored' : ''}`}
      src={featureIcon(icon)}
      alt=""
    />
  ) : (
    <span className="tool-icon tool-icon-empty" />
  );

const ToolCardContent: React.FC<ToolCardContentProps> = ({
  tool,
  step,
  note,
  alert,
  feedbackSent,
}) => (
  <>
    <div className="tool-heading">
      <div className="tool-top">
        <ToolIcon icon={tool.icon} />
        <span className="tool-step" title={`Etapa ${step}`}>
          {step}
        </span>
      </div>
      <strong className="tool-name">{tool.name}</strong>
    </div>
    <div className="tool-description">
      <p>{tool.summary}</p>
      {note && <p>{note}</p>}
    </div>
    {alert && (
      <p className="tool-notice tool-notice-alert">
        <i className="material-icons">info</i>
        {alert}
      </p>
    )}
    {feedbackSent && (
      <p className="tool-notice tool-notice-done">
        <i className="material-icons">check_circle</i>
        Feedback enviado
      </p>
    )}
  </>
);

const MutedAction: React.FC<{ icon: string }> = ({ icon, children }) => (
  <span className="tool-action tool-action-muted">
    <i className="material-icons">{icon}</i>
    {children}
  </span>
);

interface ToolCardProps {
  tool: AmbassadorTool;
  state: AmbassadorToolState;
  step: number;
  current: boolean;
  subscriber: boolean;
}

const ToolCard: React.FC<ToolCardProps> = ({ tool, state, step, current, subscriber }) => {
  const until = state.test_until ? formatAmbassadorDate(state.test_until) : '';
  const className = `ambassador-tool${current ? ' is-current' : ''}`;

  const lockedCard = (alert: string) => (
    <div className={`${className} is-locked`}>
      <ToolCardContent tool={tool} step={step} alert={alert} />
      <MutedAction icon="lock">Bloqueada</MutedAction>
    </div>
  );

  if (state.status === 'locked') {
    return lockedCard(
      isAwaitingRelease(state) && state.available_on
        ? `Disponível em ${formatAmbassadorDate(state.available_on)}.`
        : 'Libera após o feedback da ferramenta anterior.',
    );
  }

  if (state.status === 'expired') {
    // Sem feedback, o card leva ao formulário: é ele que libera a próxima.
    return state.feedback_sent ? (
      <div className={className}>
        <ToolCardContent tool={tool} step={step} note={`Teste encerrado em ${until}`} />
        <MutedAction icon="check_circle">Concluído</MutedAction>
      </div>
    ) : (
      <Link className={className} to={ambassadorFeedbackPath(tool.slug)}>
        <ToolCardContent tool={tool} step={step} note={`Teste encerrado em ${until}`} />
        <span className="tool-action">Enviar feedback</span>
      </Link>
    );
  }

  // Iniciar ou continuar o teste pede a ferramenta pronta e a assinatura do
  // boletim; o feedback de quem já testou continua aberto.
  const { link } = tool;
  if (!link) return lockedCard('Disponível em breve.');
  if (!subscriber) return lockedCard('Exclusivo para assinantes do boletim.');

  // Teste em andamento: o card abre a própria ferramenta, onde fica a barra de feedback.
  return state.status === 'testing' ? (
    <Link className={className} to={link}>
      <ToolCardContent
        tool={tool}
        step={step}
        note={`Testar até ${until}`}
        feedbackSent={state.feedback_sent}
      />
      <span className="tool-action">Continuar teste</span>
    </Link>
  ) : (
    <Link className={className} to={ambassadorToolPath(tool.slug)}>
      <ToolCardContent tool={tool} step={step} />
      <span className="tool-action">Começar teste</span>
    </Link>
  );
};

// Recolhida, a área mostra a primeira linha de cards (ou até a linha da
// ferramenta da vez); o "Ver mais" abre o restante.
const ROW_SIZE = 4;

const AmbassadorArea: React.FC = () => {
  const { profile } = useSelector<ApplicationState, ProfileState>((state) => state.storage.profile);
  const [expanded, setExpanded] = useState(false);
  const subscriber = profile.ambassador?.subscriber === true;
  const states = AMBASSADOR_TOOLS.map((tool) =>
    findAmbassadorToolState(profile.ambassador?.tools, tool.slug),
  );
  // A da vez é a última liberada (e pronta para o teste), enquanto ainda pede alguma ação.
  const lastUnlocked = states
    .map(
      (state, index) =>
        state.status === 'expired' ||
        (state.status !== 'locked' && Boolean(AMBASSADOR_TOOLS[index].link)),
    )
    .lastIndexOf(true);
  const lastState = states[lastUnlocked];
  const currentIndex =
    subscriber && lastState && !(lastState.status === 'expired' && lastState.feedback_sent)
      ? lastUnlocked
      : -1;
  const collapsedCount = Math.max(ROW_SIZE, Math.ceil((currentIndex + 1) / ROW_SIZE) * ROW_SIZE);
  const hasMore = AMBASSADOR_TOOLS.length > collapsedCount;
  const visibleTools = expanded ? AMBASSADOR_TOOLS : AMBASSADOR_TOOLS.slice(0, collapsedCount);

  return (
    <Container>
      <h4 className="pt-3">Área do embaixador:</h4>
      <div className="ambassador-card">
        <p className="ambassador-intro">
          Ferramentas antecipadas para testar, uma de cada vez. Você assiste a um vídeo curto, usa a
          ferramenta e nos conta o que achou para liberar a próxima.
        </p>
        {!subscriber && (
          <div className="alert alert-warning mb-0" role="status">
            O teste das ferramentas é exclusivo para assinantes do boletim. Com a sua assinatura
            ativa, os testes são liberados novamente.
          </div>
        )}

        {AMBASSADOR_TOOLS.length > 0 ? (
          <div className="ambassador-tools" id="ambassador-tools">
            {visibleTools.map((tool, index) => (
              <ToolCard
                key={tool.slug}
                tool={tool}
                state={states[index]}
                step={index + 1}
                current={index === currentIndex}
                subscriber={subscriber}
              />
            ))}
          </div>
        ) : (
          <p className="ambassador-message">
            Nenhuma ferramenta disponível para teste no momento. Avisaremos pela comunidade quando
            houver novidades.
          </p>
        )}

        {hasMore && (
          <button
            type="button"
            className="ambassador-more"
            aria-expanded={expanded}
            aria-controls="ambassador-tools"
            onClick={() => setExpanded((value) => !value)}
          >
            {expanded ? 'Ver menos' : 'Ver mais'}
            <i className="material-icons">{expanded ? 'expand_less' : 'expand_more'}</i>
          </button>
        )}
      </div>
    </Container>
  );
};

export default AmbassadorArea;
